import Image from 'next/image';
import { useRouter } from 'next/router';
import { useContext, useState } from 'react';
import { PrimaryButton } from '@acko-tech/building-blocks.ui.button';
import { getImageBasePath } from 'utils/generics';
import { NewPlanStages } from '../constants';
import {
    SegmentPages,
    SegmentTrackEvents,
    SegmentViewEvents,
} from 'utils/segment/constants';
import { MPosContext } from 'utils/context';
import { useSegmentViewEvents } from 'hooks/useSegmentView';
import SnackBar from 'library/snackbar';
import * as Styles from './styles';
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics';

function CustomerLink() {
    const router = useRouter();
    const { partnerId, dealerType, planDetails, setActiveStage } =
        useContext(MPosContext);

    const { customer } = planDetails;

    const [snackbar, setSnackbar] = useState({
        type: '',
        message: '',
    });
    const [isBtnLoading, setIsBtnLoading] = useState(false);

    /**
     * view segment hook
     */
    useSegmentViewEvents(SegmentViewEvents.VIEW_CUSTOMER_LINK_SHARE, {
        page: SegmentPages.NEW_PLAN_SEND_LINK_TO_CUSTOMER,
        product: 'pos',
        journey: 'buy',
        platform: 'mweb',
        partner: partnerId,
        type: dealerType,
        plan_id: planDetails.plan.plan_id,
        category: planDetails.device.type,
        serial_numbers: [planDetails.device.serial_number],
        device_model: planDetails.device.name,
    });

    const linkText = () => {
        if (window !== undefined) {
            let domain = 'https://www.acko.com';
            if (window.location.origin.includes('pos-dev')) {
                domain = 'https://sandbox2.acko.com';
            } else if (window.location.origin.includes('pos.ackodev.com')) {
                domain = 'https://www.ackodev.com';
            }
            return `${domain}/mpos/partner/${partnerId}?order_ekey=${planDetails.order_ekey}&phone=${customer.phone}`;
        }
        return `https://www.acko.com/mpos/partner/${partnerId}?order_ekey=${planDetails.order_ekey}&phone=${customer.phone}`;
    };

    const sendLink = () => {
        import('./apis').then(async (req) => {
            setIsBtnLoading(true);
            const { status, statusCode, errorMessage } =
                await req.sendLinkToCustomer(planDetails.order_ekey, partnerId);
            setIsBtnLoading(false);
            if (statusCode === 401) {
                router.push('/login');
                setSnackbar({
                    type: 'failure',
                    message: 'Token expired. Redirecting to login page.',
                });
                return;
            }
            if (status) {
                setActiveStage(NewPlanStages.CONGRATULATION);
                return;
            }
            setSnackbar({
                type: 'failure',
                message: errorMessage || 'Oops. Something went wrong',
            });
        });

        /**
         * Segment event
         */
        trackEvent(SegmentTrackEvents.TAP_BTN_SEND_TO_CUSTOMER, {
            page: SegmentPages.NEW_PLAN_SEND_LINK_TO_CUSTOMER,
            partner: partnerId,
            product: 'pos',
            journey: 'buy',
            platform: 'mweb',
            type: dealerType,
            plan_id: planDetails.plan.plan_id,
            device_model: planDetails.device.name,
            category: planDetails.device.type,
            serial_numbers: [planDetails.device.serial_number],
        });
    };

    const onCopyLink = () => {
        try {
            navigator.clipboard.writeText(linkText());
            setSnackbar({
                type: 'success',
                message: 'Link copied to clipboard',
            });
        } catch {}
    };

    return (
        <Styles.Wrapper>
            <Styles.ShareLinkImage>
                <Image
                    width={'120px'}
                    height={'120px'}
                    alt={''}
                    src={getImageBasePath('/images/shareEmail.svg')}
                />
            </Styles.ShareLinkImage>
            <Styles.Text>Send link to the customer mobile</Styles.Text>
            <Styles.PhoneNumberWrapper>
                <Styles.PhoneNumber>{customer.phone}</Styles.PhoneNumber>
                <Styles.EditIcon>
                    <Image
                        width={'20px'}
                        height={'20px'}
                        alt={''}
                        src={getImageBasePath('/icons/blueEdit.svg')}
                        onClick={() => setActiveStage(NewPlanStages.IMEI)}
                    />
                </Styles.EditIcon>
            </Styles.PhoneNumberWrapper>
            <Styles.LinkWrapper>
                <Styles.Link>{linkText().substring(0, 36)}...</Styles.Link>
                <Styles.CopyLink onClick={onCopyLink}>
                    <Image
                        src={getImageBasePath('/icons/copy.svg')}
                        width={20}
                        height={20}
                        alt={''}
                    />
                </Styles.CopyLink>
            </Styles.LinkWrapper>
            <Styles.SendButtonWrapper>
                <PrimaryButton loading={isBtnLoading} onClick={sendLink}>
                    Send to customer
                </PrimaryButton>
            </Styles.SendButtonWrapper>
            <SnackBar
                {...snackbar}
                handleSnakbarComplete={() =>
                    setSnackbar({ type: '', message: '' })
                }
            />
        </Styles.Wrapper>
    );
}

export default CustomerLink;
